import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePostingChooser } from '../contexts/PostingChooserContext';
import { usePostAdEntry } from '../hooks/usePostAdEntry';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingBag, Wrench, Sparkles, BookOpen, ChevronRight, ChevronLeft } from 'lucide-react';
import './PostingChooser.css';

const POSTING_OPTIONS = [
  {
    id: 'sell',
    icon: ShoppingBag,
    labelEn: 'Sell an item',
    labelAr: 'بيع منتج',
    descEn: 'List products in the marketplace',
    descAr: 'أعرض منتجاتك في السوق',
    path: '/add-ad',
    state: {},
    accent: '#16a34a',
    bg: 'rgba(22,163,74,0.07)',
    bgHover: 'rgba(22,163,74,0.12)',
    border: 'rgba(22,163,74,0.15)',
  },
  {
    id: 'service',
    icon: Wrench,
    labelEn: 'Offer a service',
    labelAr: 'تقديم خدمة',
    descEn: 'Advertise your professional services',
    descAr: 'أعلن عن خدماتك المهنية',
    path: '/add-ad',
    state: { default_main_category: 'services', listing_type: 'service' },
    accent: '#2563eb',
    bg: 'rgba(37,99,235,0.06)',
    bgHover: 'rgba(37,99,235,0.11)',
    border: 'rgba(37,99,235,0.14)',
  },
  {
    id: 'ladies',
    icon: Sparkles,
    labelEn: 'Post in Ladies Hub',
    labelAr: 'أضيفي في سوق السيدات',
    descEn: 'Reach the women\'s marketplace',
    descAr: 'انشري في سوق السيدات',
    path: '/add-ad',
    state: { hub: 'ladies', posting_context: 'ladies' },
    accent: '#b8860b',
    bg: 'rgba(212,168,67,0.08)',
    bgHover: 'rgba(212,168,67,0.14)',
    border: 'rgba(212,168,67,0.18)',
  },
  {
    id: 'book',
    icon: BookOpen,
    labelEn: 'Post a book',
    labelAr: 'أضف كتاباً',
    descEn: 'Share books, summaries, and study material',
    descAr: 'شارك الكتب والملخصات والمواد الدراسية',
    path: '/student-hub/books/new',
    state: {},
    accent: '#6d28d9',
    bg: 'rgba(109,40,217,0.06)',
    bgHover: 'rgba(109,40,217,0.11)',
    border: 'rgba(109,40,217,0.14)',
  },
  // Jobs / Internships / Scholarships intentionally removed from the
  // create-listing modal (2026-02-28). Routes + backend remain intact;
  // feature flags in studentHubFeatureFlags.js gate the underlying pages.
];

export { POSTING_OPTIONS };

export default function PostingChooser() {
  const { lang } = useLanguage();
  const { isOpen, hubContext, closeChooser } = usePostingChooser();
  const { triggerPostAd, DraftResumeModal } = usePostAdEntry();
  const isArabic = lang === 'ar';
  const Chevron = isArabic ? ChevronLeft : ChevronRight;

  const handleSelect = (option) => {
    closeChooser();
    // All paths go through draft pre-check
    triggerPostAd(option.path, option.state);
  };

  // Reorder: if hubContext matches an option, move it to top
  const orderedOptions = React.useMemo(() => {
    if (!hubContext) return POSTING_OPTIONS;
    const idx = POSTING_OPTIONS.findIndex(o => o.id === hubContext);
    if (idx <= 0) return POSTING_OPTIONS;
    const copy = [...POSTING_OPTIONS];
    const [item] = copy.splice(idx, 1);
    copy.unshift(item);
    return copy;
  }, [hubContext]);

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              className="pc-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={closeChooser}
              data-testid="posting-chooser-backdrop"
            />
            <motion.div
              className="pc-sheet"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 340 }}
              dir={isArabic ? 'rtl' : 'ltr'}
              data-testid="posting-chooser-sheet"
            >
              <div className="pc-handle" />

              <div className="pc-header">
                <div className="pc-header-text">
                  <h2 className="pc-title">
                    {isArabic ? 'إنشاء إعلان جديد' : 'Create a new listing'}
                  </h2>
                  <p className="pc-subtitle">
                    {isArabic
                      ? 'اختر نوع إعلانك وسنوجهك للخطوة التالية'
                      : 'Choose what you\'d like to post'}
                  </p>
                </div>
                <button
                  className="pc-close"
                  onClick={closeChooser}
                  data-testid="posting-chooser-close"
                  aria-label="Close"
                >
                  <X size={18} strokeWidth={2.5} />
                </button>
              </div>

              <div className="pc-options">
                {orderedOptions.map((option, index) => {
                  const Icon = option.icon;
                  const isHighlighted = hubContext === option.id;
                  return (
                    <motion.button
                      key={option.id}
                      className={`pc-card${isHighlighted ? ' pc-card-highlighted' : ''}`}
                      onClick={() => handleSelect(option)}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.045, duration: 0.25 }}
                      whileTap={{ scale: 0.975 }}
                      data-testid={`posting-option-${option.id}`}
                      style={{
                        '--pc-accent': option.accent,
                        '--pc-bg': option.bg,
                        '--pc-bg-hover': option.bgHover,
                        '--pc-border': option.border,
                      }}
                    >
                      <div className="pc-card-icon">
                        <Icon size={20} strokeWidth={2} />
                      </div>
                      <div className="pc-card-body">
                        <span className="pc-card-label">
                          {isArabic ? option.labelAr : option.labelEn}
                        </span>
                        <span className="pc-card-desc">
                          {isArabic ? option.descAr : option.descEn}
                        </span>
                      </div>
                      <Chevron size={16} className="pc-card-chevron" />
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Draft Resume Modal — rendered at root level */}
      {DraftResumeModal}
    </>
  );
}
